// IMG-9 Straighten: click two points along a line that should be horizontal —
// rotation-only deskew for simple scanned pages.
import { angleOf, dist, normalizeDeg, sub, type Vec } from "../core/geometry";
import type { ImageEntity } from "../doc/entities";
import type { RenderView } from "../engine/renderEntities";
import type { Tool, ToolCtx, ToolKeyEvent, ToolPointerEvent } from "./types";

export class StraightenTool implements Tool {
  readonly id = "straighten" as const;
  private targetId: string | null = null;
  private a: Vec | null = null;
  private cursor: Vec | null = null;

  constructor(private ctx: ToolCtx) {}

  setTarget(entityId: string): void {
    this.targetId = entityId;
  }

  private entity(): ImageEntity | null {
    const e = this.targetId ? this.ctx.store.doc.entities[this.targetId] : undefined;
    return e && e.type === "image" ? e : null;
  }

  activate(): void {
    this.a = null;
    this.cursor = null;
    if (!this.entity()) {
      // dead target: reset first so no input lands before the deferred switch
      queueMicrotask(() => this.ctx.switchTool("select"));
      return;
    }
    this.ctx.setMeasure("none", "");
    this.ctx.setHint("Straighten: click two points along a line that SHOULD be horizontal");
    this.ctx.setCursorTip(null);
    this.ctx.requestRender();
  }

  deactivate(): void {
    this.a = null;
  }

  /** TL-1: discard state only; the Esc handler switches tools. */
  cancel(): void {
    this.a = null;
  }

  inProgress(): boolean {
    return this.a !== null;
  }

  pointerDown(e: ToolPointerEvent): void {
    if (e.button !== 0) return;
    if (!this.entity()) return; // dead-target window before the deferred tool switch
    if (!this.a) {
      this.a = e.world; // raw world — snapping to vectors would defeat the purpose
      this.ctx.setHint("Straighten: click the second point on the same line");
      this.ctx.setCursorTip("second point on the line");
      return;
    }
    // EC-IMG-10: points too close → ask again
    if (dist(e.world, this.a) * this.ctx.scale() < 5) {
      this.ctx.setHint("Points are too close together — click further along the line");
      return;
    }
    const entity = this.entity();
    if (!entity) return;
    let delta = normalizeDeg(angleOf(sub(e.world, this.a)));
    if (delta > 90) delta -= 180; // rotate the short way
    if (delta > 90) delta -= 180;
    this.ctx.store.dispatch({
      kind: "updateEntities",
      patches: [
        { id: entity.id, before: { rotationDeg: entity.rotationDeg }, after: { rotationDeg: entity.rotationDeg - delta } }
      ]
    });
    this.ctx.switchTool("select");
  }

  pointerMove(e: ToolPointerEvent): void {
    this.cursor = e.world;
    this.ctx.requestRender();
  }

  pointerUp(): void {}

  doubleClick(): void {}

  key(e: ToolKeyEvent): boolean {
    if (e.key === "Escape") {
      this.cancel();
      this.ctx.switchTool("select");
      return true;
    }
    return false;
  }

  measurement(): boolean {
    return false;
  }

  renderDynamic(v: RenderView): void {
    if (!this.a || !this.cursor) return;
    const { ctx } = v;
    ctx.strokeStyle = "#e0b34d";
    ctx.lineWidth = 1.5 / v.cam.scale;
    ctx.setLineDash([4 / v.cam.scale, 3 / v.cam.scale]);
    ctx.beginPath();
    ctx.moveTo(this.a.x, this.a.y);
    ctx.lineTo(this.cursor.x, this.cursor.y);
    ctx.stroke();
    ctx.setLineDash([]);
  }
}
